import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import AppContext from '../../../contexts/AppContext'
import HttpClient from '../../../services/HttpClient'
import { parseBBCode } from '../../../libraries/BBCode'
import BBCodeEditor from '../../../components/BBCodeEditor/BBCodeEditor'
import { parchmentConfirm } from '../../adventure/shared/parchmentConfirm'
import MissionInviteAutocomplete from './MissionInviteAutocomplete'
import MissionIssuerPicker from './MissionIssuerPicker'
import MissionPersonCard, { MissionIssuerCard } from './MissionIssuerCard'
import { openMissionReactivateDialog } from './MissionReactivateDialog'
import { ensureStatsReadyForRoleplay } from '../../../utils/statsReallocGate'
import './MissionBoard.css'

const MISSION_CARD_VARIANT = 'edict'

const TABS = [
  { id: 'active', label: 'Activas' },
  { id: 'in_progress', label: 'En curso' },
  { id: 'archived', label: 'Archivadas' },
]

const STATUS_LABELS = {
  Active: 'Activa',
  PendingIssuer: 'Pendiente ficha',
  InProgress: 'En curso',
  Completed: 'Completada',
  Failed: 'Fallida',
  Aborted: 'Abortada',
  Archived: 'Archivada',
}

const SUPPORTED_MISSION_TERRITORY_TYPES = new Set(['han', 'shogunato', 'capital'])

function normalizeTerritoryType(value) {
  return String(value || '').trim().toLowerCase()
}

function isMissionTerritorySupported(territoryType) {
  const t = normalizeTerritoryType(territoryType)
  return !t || SUPPORTED_MISSION_TERRITORY_TYPES.has(t)
}

export default function MissionBoard({ territorySlug, territoryType, character, user }) {
  const navigate = useNavigate()
  const { character: ctxCharacter, user: ctxUser, setCharacter, hasPermission } = useContext(AppContext) || {}
  const currentCharacter = character || ctxCharacter || null
  const currentUser = user || ctxUser || null

  const [activeTab, setActiveTab] = useState('active')
  const [loading, setLoading] = useState(false)
  const [missions, setMissions] = useState([])
  const [error, setError] = useState(null)
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [canManage, setCanManage] = useState(false)
  const [isHanScope, setIsHanScope] = useState(false)
  const [canChooseDaimyoIssuer, setCanChooseDaimyoIssuer] = useState(false)
  const [canCreateAsDaimyo, setCanCreateAsDaimyo] = useState(false)
  const [canSetCustomIssuer, setCanSetCustomIssuer] = useState(false)
  const [hasDaimyo, setHasDaimyo] = useState(false)
  const [hanName, setHanName] = useState(null)
  const [form, setForm] = useState({
    title: '',
    description: '',
    rank: 'D',
    reward_ryo: 0,
    tags: [],
    hideIssuer: false,
    hideAcceptor: false,
    issuerMode: 'character',
    issuerCharacter: null,
    issuerLabel: '',
  })
  const TAGS = ['Combate', 'Espionaje', 'Rescate', 'Exploración', 'Comercio', 'Diplomacia']
  const RANKS = ['D', 'C', 'B', 'A', 'S']

  const activeCharacterAsIssuer = useMemo(() => {
    if (!currentCharacter?.characterId) return null
    return {
      characterId: Number(currentCharacter.characterId),
      name: currentCharacter.name || currentCharacter.characterName || 'Personaje',
      slug: currentCharacter.slug || null,
      clanName: currentCharacter.clanName || currentCharacter.clan?.name || null,
      rankName: currentCharacter.rankName || currentCharacter.rank?.name || null,
    }
  }, [currentCharacter])

  const defaultIssuerMode = useCallback(() => {
    if (canCreateAsDaimyo) return 'daimyo'
    return 'character'
  }, [canCreateAsDaimyo])

  const blankForm = useCallback(
    () => ({
      title: '',
      description: '',
      rank: 'D',
      reward_ryo: 0,
      tags: [],
      hideIssuer: false,
      hideAcceptor: false,
      issuerMode: defaultIssuerMode(),
      issuerCharacter:
        defaultIssuerMode() === 'character' ? activeCharacterAsIssuer : null,
      issuerLabel: '',
    }),
    [defaultIssuerMode, activeCharacterAsIssuer]
  )

  const missionsSupported = useMemo(() => isMissionTerritorySupported(territoryType), [territoryType])

  const isHanBoard = normalizeTerritoryType(territoryType) === 'han'
  const canCreate = missionsSupported && canManage

  const missionsUrl = useCallback(() => {
    let url = `/api/v1/missions?territorySlug=${encodeURIComponent(territorySlug)}&tab=${encodeURIComponent(activeTab)}`
    if (currentCharacter?.characterId) {
      url += `&characterId=${encodeURIComponent(currentCharacter.characterId)}`
    }
    return url
  }, [territorySlug, activeTab, currentCharacter?.characterId])

  const refreshMissions = useCallback(async () => {
    if (!territorySlug || !missionsSupported) {
      setMissions([])
      return
    }
    const { data } = await HttpClient().get(missionsUrl())
    setMissions(Array.isArray(data) ? data : [])
  }, [territorySlug, missionsSupported, missionsUrl])

  useEffect(() => {
    let active = true
    const loadManage = async () => {
      if (!territorySlug || !currentUser || !missionsSupported) {
        if (active) {
          setCanManage(false)
          setIsHanScope(false)
          setCanChooseDaimyoIssuer(false)
          setCanCreateAsDaimyo(false)
          setCanSetCustomIssuer(false)
          setHasDaimyo(false)
          setHanName(null)
        }
        return
      }
      try {
        const { data } = await HttpClient().get(
          `/api/v1/missions/manage-check?territorySlug=${encodeURIComponent(territorySlug)}`
        )
        if (active) {
          setCanManage(Boolean(data?.canManage))
          setIsHanScope(Boolean(data?.isHanScope))
          setCanChooseDaimyoIssuer(Boolean(data?.canChooseDaimyoIssuer))
          setCanCreateAsDaimyo(Boolean(data?.canCreateAsDaimyo))
          setCanSetCustomIssuer(Boolean(data?.canSetCustomIssuer ?? data?.canManage))
          setHasDaimyo(Boolean(data?.hasDaimyo))
          setHanName(data?.hanName ? String(data.hanName) : null)
        }
      } catch {
        if (active) {
          const perms = Array.isArray(currentUser?.permissions) ? currentUser.permissions : []
          const canStaff =
            (typeof hasPermission === 'function' &&
              (hasPermission('mission.create') || hasPermission('admin.dashboard'))) ||
            perms.includes('mission.create') ||
            perms.includes('admin.dashboard')
          setCanManage(canStaff)
          setIsHanScope(isHanBoard)
          setCanChooseDaimyoIssuer(false)
          setCanCreateAsDaimyo(false)
          setCanSetCustomIssuer(canStaff)
          setHasDaimyo(false)
          setHanName(null)
        }
      }
    }
    loadManage()
    return () => {
      active = false
    }
  }, [territorySlug, currentUser, missionsSupported, hasPermission, isHanBoard])

  useEffect(() => {
    let active = true
    const fetchMissions = async () => {
      if (!territorySlug || !missionsSupported) {
        setMissions([])
        return
      }
      setLoading(true)
      setError(null)
      try {
        const { data } = await HttpClient().get(missionsUrl())
        if (!active) return
        setMissions(Array.isArray(data) ? data : [])
      } catch (e) {
        if (!active) return
        setError('No se pudieron cargar las misiones')
        setMissions([])
      } finally {
        if (active) setLoading(false)
      }
    }
    fetchMissions()
    return () => {
      active = false
    }
  }, [territorySlug, missionsSupported, missionsUrl])

  const handleToggleTag = (t) => {
    const set = new Set(form.tags || [])
    if (set.has(t)) set.delete(t)
    else set.add(t)
    setForm((f) => ({ ...f, tags: Array.from(set) }))
  }

  const resetForm = () => {
    setForm(blankForm())
    setEditingId(null)
  }

  const openCreateForm = () => {
    resetForm()
    setShowCreate(true)
  }

  const closeMissionForm = () => {
    setShowCreate(false)
    resetForm()
  }

  const beginEditMission = (mission) => {
    if (!mission?.id) return
    const tags = safeJsonArray(mission.tags)
    const label =
      mission.issuerLabel ||
      mission.issuer?.label ||
      (mission.issuer?.role === 'custom' ? mission.issuer?.name : '') ||
      ''
    let issuerMode = 'character'
    let issuerCharacter = null
    if (label) {
      issuerMode = 'npc'
    } else if (mission.issuer?.role === 'daimyo' && canChooseDaimyoIssuer) {
      issuerMode = 'daimyo'
    } else if (mission.issuer?.characterId) {
      issuerMode = 'character'
      issuerCharacter = {
        characterId: Number(mission.issuer.characterId),
        name: mission.issuer.name || 'Personaje',
        slug: mission.issuer.slug || null,
        clanName: mission.issuer.clanName || null,
        rankName: mission.issuer.rankName || null,
      }
    } else {
      issuerMode = 'character'
      issuerCharacter = activeCharacterAsIssuer
    }

    setEditingId(Number(mission.id))
    setForm({
      title: mission.title || '',
      description: mission.description || '',
      rank: String(mission.rank || 'D').toUpperCase(),
      reward_ryo: Number(mission.reward_ryo || 0),
      tags,
      hideIssuer: Boolean(mission.hideIssuer),
      hideAcceptor: Boolean(mission.hideAcceptor),
      issuerMode,
      issuerCharacter,
      issuerLabel: label,
    })
    setShowCreate(true)
    window.requestAnimationFrame?.(() => {
      document.querySelector('.mission-create')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    })
  }

  const setIssuerMode = (mode) => {
    setForm((f) => {
      const next = { ...f, issuerMode: mode }
      if (mode === 'character') {
        next.issuerLabel = ''
        if (!next.issuerCharacter) next.issuerCharacter = activeCharacterAsIssuer
      } else if (mode === 'daimyo') {
        next.issuerLabel = ''
        next.issuerCharacter = null
      } else if (mode === 'npc') {
        next.issuerCharacter = null
      }
      return next
    })
  }

  const handleCreate = async (e) => {
    e?.preventDefault?.()
    if (!canCreate) return

    const mode = canSetCustomIssuer ? form.issuerMode : 'character'
    if (mode === 'character' && !form.issuerCharacter?.characterId && !activeCharacterAsIssuer) {
      toast.error('Selecciona un personaje emisor')
      return
    }
    if (mode === 'npc' && !String(form.issuerLabel || '').trim()) {
      toast.error('Indica el nombre del NPC emisor')
      return
    }
    if (mode === 'daimyo' && !hasDaimyo) {
      toast.error('Este Han no tiene Daimyo asignado')
      return
    }
    if (!String(form.title || '').trim()) {
      toast.error('El título es obligatorio')
      return
    }
    if (!String(form.description || '').trim()) {
      toast.error('La descripción es obligatoria')
      return
    }

    setCreating(true)
    try {
      const payload = {
        title: form.title,
        description: form.description,
        rank: form.rank,
        reward_ryo: Number(form.reward_ryo || 0),
        reward_materials: 0,
        tags: Array.isArray(form.tags) ? form.tags : [],
        hideIssuer: Boolean(form.hideIssuer),
        hideAcceptor: Boolean(form.hideAcceptor),
        territorySlug,
        characterId: currentCharacter?.characterId || null,
      }

      if (canSetCustomIssuer) {
        if (mode === 'npc') {
          payload.issuerLabel = String(form.issuerLabel).trim().slice(0, 120)
          payload.issuerAsDaimyo = false
        } else if (mode === 'daimyo') {
          payload.issuerLabel = null
          payload.issuerAsDaimyo = true
        } else {
          payload.issuerLabel = null
          payload.issuerAsDaimyo = false
          payload.issuerCharacterId =
            form.issuerCharacter?.characterId || activeCharacterAsIssuer?.characterId || null
        }
      }

      if (editingId) {
        await HttpClient().patch(`/api/v1/missions/${editingId}`, payload)
        toast.success('Misión actualizada')
      } else {
        const { data } = await HttpClient().post('/api/v1/missions', payload)
        if (data?.status === 'PendingIssuer') {
          toast.success('Misión creada (pendiente ficha del emisor; oculta al público)')
        } else {
          toast.success('Misión creada correctamente')
        }
      }
      await refreshMissions()
      closeMissionForm()
    } catch (e2) {
      toast.error(
        e2?.response?.data?.error ||
          e2?.message ||
          (editingId ? 'Error actualizando misión' : 'Error creando misión')
      )
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteMission = (missionId) => {
    if (!canManage || !missionId) return
    parchmentConfirm({
      title: 'Eliminar misión',
      message: '¿Eliminar esta misión del tablón? Solo es posible si nadie la ha aceptado.',
      cancelLabel: 'Cancelar',
      confirmLabel: 'Eliminar',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await HttpClient().delete(`/api/v1/missions/${missionId}`)
          if (editingId && Number(editingId) === Number(missionId)) closeMissionForm()
          await refreshMissions()
          toast.success('Misión eliminada')
        } catch (e) {
          toast.error(e?.response?.data?.error || e?.message || 'No se pudo eliminar la misión')
        }
      },
    })
  }

  const handleAccept = async (missionId) => {
    if (!currentCharacter?.characterId) return
    try {
      await HttpClient().post(`/api/v1/missions/${missionId}/accept`, {
        characterId: currentCharacter.characterId,
      })
      setActiveTab('in_progress')
      await refreshMissions()
      toast.success('Misión aceptada')
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.message || 'No fue posible aceptar la misión')
    }
  }

  const handleCreateIssuerSheet = (mission) => {
    if (!mission?.id) return
    const pending =
      Boolean(mission.pendingIssuerSheet) ||
      Boolean(mission.issuer?.pendingSheet) ||
      String(mission.status) === 'PendingIssuer' ||
      (Boolean(mission.issuer?.label || mission.issuerLabel) && !mission.issuer?.slug)
    const allowed =
      Boolean(mission.canCreateIssuerSheet) || ((canManage || canCreate) && pending)
    if (!allowed) {
      toast.error('No tienes permiso para crear la ficha del emisor')
      return
    }
    const issuerName = mission.issuer?.name || mission.issuerLabel || 'NPC'
    parchmentConfirm({
      title: 'Crear ficha de emisor',
      message: `Se creará un personaje de evento auto-aprobado para «${issuerName}» y la misión pasará a pública.`,
      confirmLabel: 'Crear ficha',
      onConfirm: async () => {
        try {
          const { data } = await HttpClient().post(
            `/api/v1/missions/${mission.id}/issuer-character`,
            {}
          )
          await refreshMissions()
          toast.success('Ficha creada; la misión ya es pública')
          const sheetSlug = data?.character?.slug
          if (sheetSlug) {
            navigate(`/personajes/${sheetSlug}/npc?edit=1`, {
              state: {
                from: territorySlug ? `/han/${territorySlug}` : '/',
              },
            })
          }
        } catch (e) {
          toast.error(
            e?.response?.data?.error || e?.message || 'No se pudo crear la ficha del emisor'
          )
        }
      },
    })
  }

  const handleStart = async (missionId) => {
    if (
      !(await ensureStatsReadyForRoleplay({
        character: currentCharacter,
        navigate,
        setCharacter,
      }))
    ) {
      return
    }
    navigate(`/aventura/crear/${territorySlug}?missionId=${encodeURIComponent(missionId)}`)
  }

  const handleGoToChronicle = (slug) => {
    if (slug) navigate(`/aventura/${slug}`)
  }

  const handleInvite = async (missionId, invitee) => {
    if (!invitee?.characterId || !currentCharacter?.characterId) {
      toast.error('Selecciona un personaje de la lista')
      return
    }
    try {
      await HttpClient().post(`/api/v1/missions/${missionId}/invitations`, {
        inviterCharacterId: currentCharacter.characterId,
        inviteeCharacterId: invitee.characterId,
      })
      await refreshMissions()
      toast.success(`Invitación enviada a ${invitee.name}`)
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.message || 'No se pudo enviar la invitación')
    }
  }

  const handleAcceptInvitation = async (missionId, invId) => {
    if (!currentCharacter?.characterId) return
    try {
      await HttpClient().post(`/api/v1/missions/${missionId}/invitations/${invId}/accept`, {
        inviteeCharacterId: currentCharacter.characterId,
      })
      await refreshMissions()
      toast.success('Invitación aceptada')
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.message || 'No se pudo aceptar la invitación')
    }
  }

  const handleProposeClosure = async (missionId, outcome) => {
    if (!currentCharacter?.characterId) return
    try {
      await HttpClient().post(`/api/v1/missions/${missionId}/closure-requests`, {
        characterId: currentCharacter.characterId,
        outcome,
      })
      await refreshMissions()
      toast.success('Propuesta enviada; un GM debe verificarla')
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.message || 'No se pudo proponer el cierre')
    }
  }

  const handleApproveClosure = async (missionId, reqId) => {
    try {
      await HttpClient().post(`/api/v1/missions/${missionId}/closure-requests/${reqId}/approve`)
      await refreshMissions()
      toast.success('Propuesta aprobada')
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.message || 'Error al aprobar')
    }
  }

  const handleRejectClosure = async (missionId, reqId) => {
    try {
      await HttpClient().post(`/api/v1/missions/${missionId}/closure-requests/${reqId}/reject`)
      await refreshMissions()
      toast.success('Propuesta rechazada')
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.message || 'Error al rechazar')
    }
  }

  const handleFailAbort = async (missionId, action) => {
    try {
      await HttpClient().post(`/api/v1/missions/${missionId}/${action}`)
      await refreshMissions()
      toast.success(action === 'fail' ? 'Misión marcada como fallida' : 'Misión abortada')
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.message || 'No se pudo cerrar la misión')
    }
  }

  const handleReactivate = (mission) => {
    if (!mission?.id) return
    openMissionReactivateDialog({
      mission,
      territorySlug,
      canSetCustomIssuer,
      canChooseDaimyo: isHanScope && canChooseDaimyoIssuer,
      hasDaimyo,
      hanName,
      activeCharacterAsIssuer,
      onConfirm: async (payload) => {
        await HttpClient().post(`/api/v1/missions/${mission.id}/reactivate`, {
          ...payload,
          characterId:
            payload.characterId ||
            currentCharacter?.characterId ||
            activeCharacterAsIssuer?.characterId ||
            null,
        })
        setActiveTab('active')
        await refreshMissions()
        toast.success('Misión republicada como reintento')
      },
    })
  }

  const canAccept = (mission) => {
    if (!currentCharacter?.characterId) return { allowed: false, reason: 'Debes seleccionar un personaje' }
    if (mission?.pendingIssuerSheet || String(mission?.status) === 'PendingIssuer') {
      return { allowed: false, reason: 'Pendiente de ficha del emisor' }
    }
    if (mission?.isTaken && !mission?.isAcceptor) {
      const name = mission?.primaryAcceptor?.name || 'otro shinobi'
      return { allowed: false, reason: `Asignada a ${name}` }
    }
    if (mission?.myAcceptance) {
      return { allowed: false, reason: 'Ya has interactuado con esta misión' }
    }
    if (String(mission?.status) !== 'Active') {
      return { allowed: false, reason: 'Esta misión no está disponible' }
    }
    const scope = String(mission?.scope || '').trim().toLowerCase()
    if (scope === 'han' || (!mission?.shogunatoId && mission?.hanId)) {
      const charHan = currentCharacter?.homeHanId != null ? Number(currentCharacter.homeHanId) : null
      if (!charHan) {
        return { allowed: false, reason: 'Tu personaje no tiene Han de origen asignado' }
      }
      const missionHan = mission?.hanId != null ? Number(mission.hanId) : null
      if (missionHan != null && charHan !== missionHan) {
        return { allowed: false, reason: 'Tu personaje no pertenece a este Han' }
      }
      const loc =
        currentCharacter?.current_territory_slug || currentCharacter?.currentTerritorySlug || null
      if (!loc) {
        return { allowed: false, reason: 'Debes estar físicamente en este Han para aceptar' }
      }
      if (isHanBoard && String(loc).trim().toLowerCase() !== String(territorySlug).trim().toLowerCase()) {
        return { allowed: false, reason: 'Debes estar en este Han para aceptar la misión' }
      }
    }
    return { allowed: true }
  }

  const emptyMessage = useMemo(() => {
    if (activeTab === 'in_progress') return 'No hay misiones en curso en este tablón.'
    if (activeTab === 'archived') return 'No hay misiones archivadas.'
    return isHanBoard
      ? 'Aún no hay misiones publicadas en este Han.'
      : 'Vuelve más tarde; se publicarán nuevas misiones.'
  }, [activeTab, isHanBoard])

  if (!territorySlug) return null

  return (
    <section className={`mission-board mission-board--${MISSION_CARD_VARIANT}`}>
      <div className="mission-board__header">
        <h2 className="mission-board__title">Tablón de Misiones</h2>
        <div className="mission-board__actions">
          {canCreate && (
            <button
              type="button"
              className="mission-btn mission-btn--primary"
              onClick={() => {
                if (showCreate && !editingId) closeMissionForm()
                else openCreateForm()
              }}
              aria-expanded={showCreate}
            >
              {showCreate && !editingId ? 'Cerrar formulario' : 'Crear misión'}
            </button>
          )}
        </div>
      </div>

      <div className="mission-board__tabs" role="tablist" aria-label="Filtrar misiones">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`mission-tab ${activeTab === tab.id ? 'mission-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {showCreate && canCreate && (
        <form className="mission-create" onSubmit={handleCreate}>
          <p className="mission-create__form-title">
            {editingId ? `Modificar misión #${editingId}` : 'Nueva misión'}
          </p>
          {canSetCustomIssuer && (
            <MissionIssuerPicker
              territorySlug={territorySlug}
              issuerMode={form.issuerMode}
              issuerCharacter={form.issuerCharacter}
              issuerLabel={form.issuerLabel}
              canChooseDaimyo={isHanScope && canChooseDaimyoIssuer}
              hasDaimyo={hasDaimyo}
              hanName={hanName}
              disabled={creating}
              onModeChange={setIssuerMode}
              onCharacterChange={(issuerCharacter) =>
                setForm((f) => ({ ...f, issuerCharacter }))
              }
              onLabelChange={(issuerLabel) => setForm((f) => ({ ...f, issuerLabel }))}
            />
          )}
          <div className="field-row">
            <label className="field-label">Título</label>
            <input
              className="field-input"
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
              maxLength={150}
            />
          </div>
          <div className="field-row mission-create__description">
            <label className="field-label">Descripción</label>
            <BBCodeEditor
              value={form.description}
              onChange={(description) => setForm((f) => ({ ...f, description }))}
              placeholder="Describe la misión: objetivo, contexto y condiciones…"
              showPreview={true}
              showToolbar={true}
              height="220px"
              enableRPCode={false}
              className="mission-create__bbcode"
            />
          </div>
          <div className="field-grid">
            <div className="field-row">
              <label className="field-label">Rango</label>
              <select
                className="field-input"
                value={form.rank}
                onChange={(e) => setForm((f) => ({ ...f, rank: e.target.value }))}
              >
                {RANKS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="field-row">
              <label className="field-label">Ryō</label>
              <input
                className="field-input"
                type="number"
                min="0"
                value={form.reward_ryo}
                onChange={(e) => setForm((f) => ({ ...f, reward_ryo: e.target.value }))}
              />
            </div>
          </div>
          <div className="field-row">
            <label className="field-label">Tags</label>
            <div className="chips">
              {TAGS.map((t) => {
                const on = Array.isArray(form.tags) && form.tags.includes(t)
                return (
                  <button
                    key={t}
                    type="button"
                    className={`filter-chip ${on ? 'is-active' : ''}`}
                    aria-pressed={on}
                    onClick={() => handleToggleTag(t)}
                  >
                    {t}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="mission-create__privacy" role="group" aria-label="Visibilidad">
            <label className="mission-check">
              <input
                type="checkbox"
                checked={Boolean(form.hideIssuer)}
                onChange={(e) => setForm((f) => ({ ...f, hideIssuer: e.target.checked }))}
              />
              <span>Ocultar emisor</span>
            </label>
            <label className="mission-check">
              <input
                type="checkbox"
                checked={Boolean(form.hideAcceptor)}
                onChange={(e) => setForm((f) => ({ ...f, hideAcceptor: e.target.checked }))}
              />
              <span>Ocultar quien acepte</span>
            </label>
          </div>
          <div className="mission-create__actions">
            <button type="button" className="mission-btn" onClick={closeMissionForm}>
              Cancelar
            </button>
            <button type="submit" className="mission-btn mission-btn--primary" disabled={creating}>
              {creating
                ? editingId
                  ? 'Guardando…'
                  : 'Creando…'
                : editingId
                  ? 'Guardar cambios'
                  : 'Crear'}
            </button>
          </div>
        </form>
      )}

      <div className="mission-board__body">
        {!missionsSupported && (
          <div className="mission-empty">
            <h3>Misiones próximamente</h3>
            <p>Las misiones de este territorio estarán disponibles próximamente.</p>
          </div>
        )}

        {missionsSupported && loading && (
          <div className="mission-empty">
            <p>Cargando misiones…</p>
          </div>
        )}
        {missionsSupported && !loading && error && (
          <div className="mission-empty">
            <p>{error}</p>
          </div>
        )}
        {missionsSupported && !loading && !error && missions.length === 0 && (
          <div className="mission-empty">
            <h3>Sin misiones</h3>
            <p>{emptyMessage}</p>
          </div>
        )}

        {missionsSupported && !loading && !error && missions.length > 0 && (
          <div className="mission-grid">
            {missions.map((m, index) => {
              const tags = safeJsonArray(m.tags)
              const { allowed, reason } = canAccept(m)
              const missionStatus = String(m.status || '')
              const isAcceptor = Boolean(m.isAcceptor)
              const isTaken = Boolean(m.isTaken)
              const rank = String(m.rank || 'D').toUpperCase()
              const statusLabel = STATUS_LABELS[missionStatus] || missionStatus
              const showAcceptorClosure =
                isAcceptor && missionStatus === 'InProgress' && !m.closureRequest
              // Manager can close/review even if they are also the acceptor (no adventure / solo GM).
              const showGmDirectClosure =
                canManage && missionStatus === 'InProgress' && !m.closureRequest && !isAcceptor
              const showGmReviewClosure =
                canManage && m.closureRequest?.status === 'Pending'
              const allowCreateIssuerSheet =
                Boolean(m.canCreateIssuerSheet) ||
                ((canManage || canCreate) &&
                  (Boolean(m.pendingIssuerSheet) ||
                    Boolean(m.issuer?.pendingSheet) ||
                    missionStatus === 'PendingIssuer' ||
                    (Boolean(m.issuer?.label || m.issuerLabel) && !m.issuer?.slug)))
              const statusClass =
                missionStatus === 'Completed'
                  ? 'mission-card--completed'
                  : missionStatus === 'InProgress'
                    ? 'mission-card--accepted'
                    : missionStatus === 'Failed' || missionStatus === 'Aborted'
                      ? 'mission-card--failed'
                      : missionStatus === 'PendingIssuer'
                        ? 'mission-card--pending-issuer'
                        : ''

              return (
                <article
                  key={m.id}
                  className={`mission-card mission-card--${MISSION_CARD_VARIANT} mission-card--rank-${rank} ${statusClass}`.trim()}
                  style={{ '--mission-i': index }}
                >
                  <aside className="mission-card__seal" aria-label={`Rango ${rank}`}>
                    <span className="mission-card__rank">{rank}</span>
                    <span className="mission-card__seal-label">Rango</span>
                  </aside>

                  <div className="mission-card__main">
                    <p className="mission-card__eyebrow">Aviso oficial del tablón</p>
                    <header className="mission-card__header">
                      <div className="mission-card__topline">
                        <h3 className="mission-title">{m.title}</h3>
                        <span className="mission-card__stamp">{statusLabel}</span>
                      </div>
                      {m.pendingIssuerSheet && (
                        <p className="mission-card__pending-issuer">
                          Pendiente ficha del emisor — oculta al público hasta crear la ficha
                        </p>
                      )}
                      {m.isRetry && m.sourceMissionId && (
                        <p className="mission-card__retry">
                          Reintento de misión #{m.sourceMissionId}
                          {m.sourceOutcome ? ` (${m.sourceOutcome === 'Failed' ? 'fallida' : 'abortada'})` : ''}
                        </p>
                      )}
                      <div className="mission-card__people">
                        <MissionIssuerCard
                          issuer={m.issuer}
                          onCreateIssuerSheet={
                            allowCreateIssuerSheet
                              ? () => handleCreateIssuerSheet(m)
                              : null
                          }
                        />
                        <span className="mission-card__rewards-sep" aria-hidden="true" />
                        <MissionPersonCard
                          person={resolveAcceptorPerson(m)}
                          kicker="Acepta"
                          emptyLabel="Nadie aún"
                          ariaRoleLabel="Acepta"
                        />
                      </div>
                      <p className="mission-card__rewards">
                        <span>
                          <em>Ryō</em> {Number(m.reward_ryo || 0).toLocaleString('es-ES')}
                        </span>
                      </p>
                    </header>

                    <div
                      className="mission-desc"
                      dangerouslySetInnerHTML={{ __html: parseBBCode(m.description || '') }}
                    />

                    {m.myPendingInvitation && (
                      <div className="mission-card__invite-banner">
                        <span>Te han invitado a esta misión</span>
                        <button
                          type="button"
                          className="mission-btn mission-btn--accept"
                          onClick={() => handleAcceptInvitation(m.id, m.myPendingInvitation.id)}
                        >
                          Aceptar invitación
                        </button>
                      </div>
                    )}

                    {m.closureRequest?.status === 'Pending' && (
                      <p className="mission-card__closure-pending">
                        Propuesta de cierre ({m.closureRequest.outcome}) pendiente de GM
                      </p>
                    )}

                    {isAcceptor && m.canInvite && (
                      <div className="mission-card__invite-panel">
                        <label className="field-label">Invitar shinobi</label>
                        <MissionInviteAutocomplete
                          territorySlug={territorySlug}
                          missionId={m.id}
                          excludeCharacterId={currentCharacter?.characterId}
                          onInvite={(invitee) => handleInvite(m.id, invitee)}
                        />
                        {Array.isArray(m.invitations) && m.invitations.length > 0 && (
                          <ul className="mission-card__invite-list">
                            {m.invitations.map((inv) => (
                              <li key={inv.id}>
                                {inv.inviteeName || `#${inv.inviteeCharacterId}`} — {inv.status}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    <footer className="mission-card__footer">
                      <div className="mission-tags" aria-label="Categorías">
                        {tags.length > 0 ? (
                          tags.map((t, i) => (
                            <span key={`${m.id}-tag-${i}`} className="tag-chip">
                              {t}
                            </span>
                          ))
                        ) : (
                          <span className="tag-chip">General</span>
                        )}
                      </div>

                      <div className="mission-card__actions">
                        <button
                          type="button"
                          className="mission-btn mission-btn--ghost"
                          onClick={() =>
                            navigate(
                              `/misiones/${m.id}?territorySlug=${encodeURIComponent(territorySlug)}`
                            )
                          }
                        >
                          Ver ficha
                        </button>
                        {allowCreateIssuerSheet && (
                          <button
                            type="button"
                            className="mission-btn mission-btn--primary"
                            onClick={() => handleCreateIssuerSheet(m)}
                          >
                            Crear ficha del emisor
                          </button>
                        )}
                        {activeTab === 'active' && !isTaken && !m.myAcceptance && (
                          <button
                            type="button"
                            className="mission-btn mission-btn--accept"
                            disabled={!allowed || allowCreateIssuerSheet}
                            title={
                              allowCreateIssuerSheet
                                ? 'Crea la ficha del emisor antes de aceptar'
                                : !allowed && reason
                                  ? reason
                                  : 'Aceptar misión'
                            }
                            onClick={() => handleAccept(m.id)}
                          >
                            Aceptar
                          </button>
                        )}
                        {activeTab === 'active' && isTaken && !isAcceptor && (
                          <span className="mission-card__taken-msg">{reason || 'Asignada a otro shinobi'}</span>
                        )}
                        {m.canStart && (
                          <button
                            type="button"
                            className="mission-btn mission-btn--primary"
                            onClick={() => handleStart(m.id)}
                          >
                            Iniciar
                          </button>
                        )}
                        {m.linkedAdventureSlug && (
                          <button
                            type="button"
                            className="mission-btn mission-btn--ghost"
                            onClick={() => handleGoToChronicle(m.linkedAdventureSlug)}
                          >
                            Ir a crónica
                          </button>
                        )}
                        {showAcceptorClosure && (
                          <>
                            <button
                              type="button"
                              className="mission-btn mission-btn--ghost"
                              onClick={() => handleProposeClosure(m.id, 'Failed')}
                            >
                              Proponer fallida
                            </button>
                            <button
                              type="button"
                              className="mission-btn mission-btn--ghost"
                              onClick={() => handleProposeClosure(m.id, 'Aborted')}
                            >
                              Proponer abortada
                            </button>
                          </>
                        )}
                        {showGmReviewClosure && (
                          <>
                            <button
                              type="button"
                              className="mission-btn mission-btn--primary"
                              onClick={() => handleApproveClosure(m.id, m.closureRequest.id)}
                            >
                              Aprobar cierre
                            </button>
                            <button
                              type="button"
                              className="mission-btn mission-btn--ghost"
                              onClick={() => handleRejectClosure(m.id, m.closureRequest.id)}
                            >
                              Rechazar
                            </button>
                          </>
                        )}
                        {showGmDirectClosure && (
                          <>
                            <button
                              type="button"
                              className="mission-btn mission-btn--ghost"
                              onClick={() => handleFailAbort(m.id, 'fail')}
                            >
                              Marcar fallida
                            </button>
                            <button
                              type="button"
                              className="mission-btn mission-btn--ghost"
                              onClick={() => handleFailAbort(m.id, 'abort')}
                            >
                              Abortar
                            </button>
                          </>
                        )}
                        {m.canEdit && (
                          <button
                            type="button"
                            className="mission-btn mission-btn--ghost"
                            onClick={() => beginEditMission(m)}
                          >
                            Modificar
                          </button>
                        )}
                        {m.canDelete && (
                          <button
                            type="button"
                            className="mission-btn mission-btn--danger"
                            onClick={() => handleDeleteMission(m.id)}
                          >
                            Eliminar
                          </button>
                        )}
                        {canManage &&
                          (missionStatus === 'Failed' || missionStatus === 'Aborted') && (
                            <button
                              type="button"
                              className="mission-btn mission-btn--primary"
                              onClick={() => handleReactivate(m)}
                            >
                              Reactivar
                            </button>
                          )}
                      </div>
                    </footer>
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}

function safeJsonArray(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  try {
    const v = JSON.parse(value)
    return Array.isArray(v) ? v : []
  } catch {
    return []
  }
}

function resolveAcceptorPerson(mission) {
  const list = Array.isArray(mission?.acceptors) ? mission.acceptors : []
  const missionStatus = String(mission?.status || '')
  // Abort/fail marks acceptance Cancelled; still show who took the mission.
  const okStatuses = ['Failed', 'Aborted'].includes(missionStatus)
    ? ['Accepted', 'Completed', 'Cancelled']
    : ['Accepted', 'Completed']
  const visible = list.filter(
    (a) => !a.hidden && a.name && okStatuses.includes(String(a.status || ''))
  )
  if (visible.length) {
    const primary = visible[0]
    return {
      hidden: false,
      name: primary.name,
      slug: primary.slug || null,
      portrait: primary.portrait || null,
      clanName: primary.clanName || null,
      rankName: primary.rankName || null,
      extraCount: visible.length > 1 ? visible.length - 1 : 0,
    }
  }
  const count = Number(mission?.acceptorCount || 0)
  if (count > 0) {
    return { hidden: true }
  }
  return null
}
